import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMemo, useState } from 'react';
import { useGetCashUpReportsByDateRange } from '../../../hooks/reports/queries';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import { ICashUpReport } from '../../../interfaces/Store';
import { exportCashUpReportsByDateRange } from '../../../services/reports.service';
import { useLocation, useHistory } from 'react-router-dom';

const CashupReportsReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const history = useHistory();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();

  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const fromDateParam = searchParams.get('fromDate') || undefined;
  const toDateParam = searchParams.get('toDate') || undefined;
  const fromDateISO = fromDateParam
    ? moment(fromDateParam, 'YYYY-MM-DD').startOf('day').toISOString()
    : undefined;
  const toDateISO = toDateParam
    ? moment(toDateParam, 'YYYY-MM-DD').endOf('day').toISOString()
    : undefined;
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>([
    fromDateParam ? moment(fromDateParam, 'YYYY-MM-DD').toDate() : moment().startOf('month').toDate(),
    toDateParam ? moment(toDateParam, 'YYYY-MM-DD').toDate() : moment().endOf('month').toDate()
  ]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetCashUpReportsByDateRange(searchParamStoreId, {
    fromDate: fromDateISO,
    toDate: toDateISO,
    cashierBoxId: undefined
  });

  const cashupsReports = useMemo(() => {
    if (!data) return [];
    return data?.cashUpReports || [];
  }, [data]);

  const totalRowsCount = useMemo(() => data?.totalRows || 0, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  const onDateChangeURL = (value: [DateValue, DateValue]) => {
    setDateRange(value);
    const [start, end] = value;
    if (!start || !end) return;
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('fromDate', moment(start).format('YYYY-MM-DD'));
    nextParams.set('toDate', moment(end).format('YYYY-MM-DD'));
    history.replace({ pathname: location.pathname, search: nextParams.toString() });
  };
  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:CashupReportsReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={onDateChangeURL}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportCashUpReportsByDateRange(searchParamStoreId, {
                      fromDate: fromDateISO,
                      toDate: toDateISO
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            storeId={searchParamStoreId}
            tableInfoData={cashupsReports as ICashUpReport[]}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default CashupReportsReport;
